import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Col,
  Icon,
  IconButton,
  Label,
  Row,
  Select,
  TextField,
  TextArea,
  Tooltip,
} from '@folio/stripes/components';
import { Field } from 'react-final-form';
import {
  createIntl,
  createIntlCache,
  FormattedMessage,
  useIntl,
} from 'react-intl';
import { uniqueId } from 'lodash';
import { required as requiredValidator } from '../../helpers/Validations';
import useTenantLocales from '../../hooks/useTenantLocales';
import styles from './LocalizedTextField.css';

// Fallback used only when tenantLocales prop is explicitly passed
const DEFAULT_LOCALES = [
  { value: 'ar',    label: 'العربية' },
  { value: 'en-US', label: 'English' },
];

function LocalizedTextField({
  fields,
  fieldLabel,
  fieldComponent,
  fieldComponentProps,
  isRequired,
  tenantLocales,
  rowStyle,
}) {
  const intl = useIntl();
  const { localeOptions: fetchedLocales } = useTenantLocales();

  // Use explicitly passed tenantLocales, then fetched, then hardcoded defaults
  const availableLocales = tenantLocales || fetchedLocales || DEFAULT_LOCALES;

  const getLocaleLabel = (localeValue) => {
    const cache = createIntlCache();
    const locale = {
      intl: createIntl(
        {
          locale: localeValue,
          messages: {},
        },
        cache,
      ),
    };

    return intl.formatDisplayName(localeValue, { type: 'language' }) !==
      undefined
      ? `${intl.formatDisplayName(localeValue, {
          type: 'language',
        })} - ${locale.intl.formatDisplayName(localeValue, {
          type: 'language',
        })}`
      : intl.formatMessage(
          {
            id: `stripes-core.ul.button.languageName.${localeValue}`,
          },
          {},
          localeValue
        );
  };

  const getDataOptions = () => {
    return availableLocales?.map((locale) => ({
      label: getLocaleLabel(locale.value),
      value: locale.value,
      disabled:
        fields.value.find((field) => field.locale === locale.value) !==
        undefined,
    }));
  };

  const renderFieldArray = () => {
    if (!fields.length) {
      return (
        <div className={styles.emptyMsg}>
          <FormattedMessage
            id="ui-reports.localizedTextField.emptyMsg"
            values={{
              fieldLabel: <strong>{fieldLabel}</strong>,
              icon: <Icon icon="plus-sign" />,
            }}
            defaultMessage="No field labels found, click on {icon} icon to add localized {fieldLabel}"
          />
        </div>
      );
    }

    return (
      <>
        {fields.map((fieldName, index) => (
          <Row key={`${fieldName}-localized-text-field${index}`} style={rowStyle}>
            <Col xs={6}>
              <Field
                id={`reports-localized-field-${fieldName}-${index}-locale-value`}
                component={fieldComponent}
                fullWidth
                name={`${fieldName}.value`}
                required
                validate={requiredValidator}
                type="text"
                {...(fieldComponentProps || {})}
              />
            </Col>
            <Col xs={4}>
              <Field
                id={`reports-localized-field-${fieldName}-${index}-select-locale`}
                component={Select}
                name={`${fieldName}.locale`}
                placeholder={intl.formatMessage({
                  id: 'ui-reports.localizedTextField.language.placeholder',
                  defaultMessage: 'Choose language',
                })}
                dataOptions={getDataOptions()}
                required
                validate={requiredValidator}
              />
            </Col>
            <Col xs={2} className={styles.remove_btn}>
              <Button
                id={`reports-localized-field-remove-${fieldName}-${index}-btn`}
                buttonStyle="slim"
                disabled={fields.value.length === 1}
                onClick={() => fields.remove(index)}
              >
                <Icon icon="trash" />
              </Button>
            </Col>
          </Row>
        ))}
      </>
    );
  };

  return (
    <div className={styles.container}>
      <div>
        <Row>
          <Col xs={6}>
            <Label required={isRequired}>{fieldLabel}</Label>
          </Col>
          <Col xs={6} className={styles.infoIcon}>
            <Tooltip
              id="localized-field-add-tooltip"
              text={
                <FormattedMessage
                  id="ui-reports.localizedTextField.addLabel"
                  values={{ fieldLabel }}
                  defaultMessage="Add new {fieldLabel}"
                />
              }
            >
              {({ ref, ariaIds }) => (
                <IconButton
                  id={uniqueId('reports-localized-field-add-btn')}
                  onClick={() => fields.push({})}
                  disabled={fields?.value?.length === availableLocales?.length}
                  icon="plus-sign"
                  aria-labelledby={ariaIds.text}
                  ref={ref}
                />
              )}
            </Tooltip>
            <Tooltip
              id="localized-field-info-tooltip"
              text={
                <FormattedMessage
                  id="ui-reports.localizedTextField.info.content"
                  values={{
                    fieldName: <strong>{fieldLabel}</strong>,
                  }}
                  defaultMessage="You can enter {fieldName} field in more than one language. Then use these values when displaying according to the interface language."
                />
              }
            >
              {({ ref, ariaIds }) => (
                <IconButton
                  id={uniqueId('reports-localized-field-info-btn')}
                  icon="info"
                  aria-labelledby={ariaIds.text}
                  ref={ref}
                />
              )}
            </Tooltip>
          </Col>
        </Row>
      </div>
      <div className={styles.fieldArray}>{renderFieldArray()}</div>
    </div>
  );
}

LocalizedTextField.propTypes = {
  fields:              PropTypes.object.isRequired,
  fieldLabel:          PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  fieldComponent:      PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  fieldComponentProps: PropTypes.object,
  rowStyle:            PropTypes.object,
  isRequired:          PropTypes.bool,
  tenantLocales:       PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string,
    })
  ),
};

LocalizedTextField.defaultProps = {
  fieldComponent: TextField,
  fieldComponentProps: {},
  rowStyle:            undefined,
  isRequired: false,
  tenantLocales: null,
};

export default LocalizedTextField;
